import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent, Avatar, Chip,
  LinearProgress, CircularProgress, Tooltip, IconButton, Paper, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Fade, Stack, TablePagination,
  AppBar, Toolbar, FormControl, InputLabel, Select, MenuItem, InputAdornment,
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  RefreshOutlined as RefreshIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { TextField } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const COLORS = {
  primary: '#0B1F3D',
  accent: '#1976D2',
  healthy: '#2E7D32',
  warning: '#ED6C02',
  danger: '#C62828',
  low: '#0288D1',
  surface: '#F8FAFC',
  border: '#E2E8F0',
};

const getLoadBandColor = (band, utilPct) => {
  if (!band) {
    if (utilPct >= 80) return { bg: '#E8F5E9', text: '#2E7D32', label: 'Healthy' };
    if (utilPct >= 50) return { bg: '#FFF8E1', text: '#F57F17', label: 'Moderate' };
    return { bg: '#FFF3E0', text: '#E65100', label: 'Low' };
  }
  if (band === 'Healthy') return { bg: '#E8F5E9', text: '#2E7D32', label: 'Healthy' };
  if (band === 'Overloaded') return { bg: '#FFEBEE', text: '#C62828', label: 'Overloaded' };
  return { bg: '#FFF8E1', text: '#F57F17', label: 'Low' };
};

const StatCard = ({ icon, title, value, subtitle, color, trend }) => (
  <Card sx={{
    borderRadius: 3, boxShadow: '0 2px 12px rgba(11,31,61,0.08)',
    border: `1px solid ${COLORS.border}`, height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(11,31,61,0.14)' }
  }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, mb: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: COLORS.primary, lineHeight: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.78rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: color + '22', width: 52, height: 52 }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 26 } })}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const TeacherRow = ({ teacher, index }) => {
  const band = getLoadBandColor(teacher.loadBand || teacher.loadStatus, teacher.utilizationPercent);
  const utilPct = teacher.utilizationPercent || 0;
  return (
    <TableRow hover sx={{ '&:last-child td': { border: 0 }, transition: 'background 0.15s' }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 34, height: 34, bgcolor: COLORS.primary, fontSize: '0.8rem', fontWeight: 700 }}>
            {teacher.teacherName?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>
              {teacher.teacherName}
            </Typography>
            {teacher.isClassTeacher === 'Yes' && (
              <Chip label="Class Teacher" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600, mt: 0.2 }} />
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.primary }}>
          {teacher.totalAssignedPeriods ?? teacher.assignedPeriods ?? 0}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" color="text.secondary">
          {teacher.totalAvailablePeriods ?? teacher.availablePeriods ?? 0}
        </Typography>
      </TableCell>
      <TableCell sx={{ width: 180 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(utilPct, 100)}
            sx={{
              flex: 1, height: 8, borderRadius: 4,
              bgcolor: '#EEF2F7',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: utilPct >= 80 ? COLORS.healthy : utilPct >= 40 ? COLORS.warning : COLORS.accent
              }
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 38, textAlign: 'right', color: COLORS.primary }}>
            {utilPct.toFixed(1)}%
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={band.label}
          size="small"
          sx={{ bgcolor: band.bg, color: band.text, fontWeight: 700, fontSize: '0.72rem', borderRadius: 1.5 }}
        />
      </TableCell>
    </TableRow>
  );
};

const getDefaultFromDate = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d.toISOString().split('T')[0];
};
const getDefaultToDate = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

export default function TeacherLoadOverview() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageOptions = [5, 10, 15];
  const [summary, setSummary] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');

  // ── Branch state (mirrors Employee.js pattern) ──────────────────────────
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
   // ── Date range for proxy-aware reporting ────────────────────────────────
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate,   setToDate]   = useState(getDefaultToDate());

  // Filter and paginate
  const filteredSummary = summary.filter(teacher =>
    teacher.teacherName?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const paginatedSummary = filteredSummary.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ── Fetch branch list (master users only) ─────────────────────────────
  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBranches(data);

        // Auto-fill first branch for master user when branches load
        if (data.length > 0) {
          setSelectedBranchId(data[0].branchId);
        }
      } else {
        console.error('Failed to fetch branches');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // ── Fetch teacher load data — branchId sent as query param ────────────
  // APIs integrated on this page:
  //   1. GET /adv/teacher-load/summary?Branch_id={branchId}
  //   2. GET /adv/teacher-load/heatmap?Branch_id={branchId}
  // Branch id is appended as a query parameter to both endpoints,
  // consistent with the pattern used in Employee.js (/Employee/GetEmployee?Branch_id=)
  const fetchData = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');

      // Resolve the active branch id: master users use dropdown selection,
      // non-master users read from session storage (set at login)
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');

    const summaryParams = new URLSearchParams({ Branch_id: branchId });
      const heatmapParams = new URLSearchParams({ Branch_id: branchId });
      if (fromDate) { summaryParams.set('fromDate', fromDate); heatmapParams.set('fromDate', fromDate); }
      if (toDate)   { summaryParams.set('toDate',   toDate);   heatmapParams.set('toDate',   toDate);   }

      const [summaryRes, heatmapRes] = await Promise.all([
        fetch(`${apiUrl}/adv/teacher-load/summary?${summaryParams}`, {
          headers: { Authorization: token },
        }),
        fetch(`${apiUrl}/adv/teacher-load/heatmap?${heatmapParams}`, {
          headers: { Authorization: token },
        }),
      ]);

      setSummary(await summaryRes.json().then(d => Array.isArray(d) ? d : []));
      setHeatmap(await heatmapRes.json().then(d => Array.isArray(d) ? d : []));
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  // ── On mount: detect master user, load branches or set branch from session ─
  useEffect(() => {
    const isMasterUser = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      // fetchBranches will set selectedBranchId → triggers the second useEffect
      fetchBranches();
    } else {
      // Non-master: read branchId directly from session and set it,
      // which will trigger the second useEffect to fetch data
      const branchId = sessionStorage.getItem('branchId');
      setSelectedBranchId(branchId);
    }
  }, []);

// ── Re-fetch data whenever selectedBranchId or date range changes ─
  useEffect(() => {
    if (selectedBranchId) {
      fetchData();
    }
  }, [selectedBranchId, fromDate, toDate]);

  const totalTeachers = summary.length;
  const activeTeachers = summary.filter(t => (t.totalAssignedPeriods || 0) > 0).length;
  const avgUtilization = summary.length
    ? (summary.reduce((s, t) => s + (t.utilizationPercent || 0), 0) / summary.length).toFixed(1)
    : 0;
  const healthyCount = heatmap.filter(t => t.loadBand === 'Healthy').length;
  const overloadedCount = heatmap.filter(t => t.loadBand === 'Overloaded' && (t.weeklyLoad || 0) > 0).length;

  const pieData = [
    { name: 'Healthy', value: healthyCount, color: COLORS.healthy },
    { name: 'Under-utilized', value: totalTeachers - healthyCount - overloadedCount, color: COLORS.warning },
    { name: 'Unassigned', value: summary.filter(t => (t.totalAssignedPeriods || 0) === 0).length, color: '#B0BEC5' },
  ].filter(d => d.value > 0);

  const barData = [...summary]
    .sort((a, b) => (b.totalAssignedPeriods || 0) - (a.totalAssignedPeriods || 0))
    .slice(0, 12)
    .map(t => ({
      name: t.teacherName?.split(' ')[0],
      assigned: t.totalAssignedPeriods || 0,
      free: t.freePeriods || 0,
      util: t.utilizationPercent || 0,
    }));

  return (
    <Box sx={{ bgcolor: COLORS.surface, minHeight: '100vh', pb: 4 }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ background: `linear-gradient(135deg, ${COLORS.primary} 0%, #1a3a6b 100%)`, py: 3.5, px: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

          {/* Left: Title + subtitle */}
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: -0.5 }}>
              Teacher Load Overview
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.3 }}>
              Workload distribution & utilization at a glance
            </Typography>
          </Box>

          {/* Right: Branch Dropdown (master users only) + Refresh button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

            {/* ── Branch Dropdown — visible only for Master/Admin users ── */}
            {isMaster && (
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    top: '-2px',
                    '&.Mui-focused': { color: '#64B5F6' },
                    '&.MuiFormLabel-filled': { color: 'rgba(255,255,255,0.7)' },
                  }}
                >
                  Branch
                </InputLabel>
                <Select
                  value={selectedBranchId || ''}
                  onChange={(e) => {
                    setSelectedBranchId(e.target.value);
                    setPage(0); // Reset table pagination when branch changes
                  }}
                  label="Branch"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }} />
                    </InputAdornment>
                  }
                  sx={{
                    color: '#ffffff',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    letterSpacing: '0.3px',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.15)',
                      borderWidth: '1px',
                      transition: 'border-color 0.2s ease',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.11)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.35)',
                      },
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#64B5F6',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255,255,255,0.5)',
                      transition: 'color 0.2s ease',
                    },
                    '&.Mui-focused .MuiSelect-icon': { color: '#64B5F6' },
                    '& .MuiSelect-select': { py: 1.1 },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: '1px solid rgba(11,31,61,0.12)',
                        boxShadow: '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                        '& .MuiList-root': { py: 0.5 },
                      },
                    },
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId}
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#0B1F3D',
                        px: 2,
                        py: 1.1,
                        mx: 0.5,
                        borderRadius: '6px',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: 'rgba(11,31,61,0.07)' },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25,118,210,0.1)',
                          color: '#1565C0',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: 'rgba(25,118,210,0.15)' },
                        },
                      }}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Refresh button — also respects current selectedBranchId via fetchData() */}
{/* ── Date Range Filter ── */}
            <TextField
              label="From" type="date" size="small"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)', color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
                '& input': { color: '#fff' },
              }}
            />
            <TextField
              label="To" type="date" size="small"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)', color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
                '& input': { color: '#fff' },
              }}
            />

            {/* Refresh button — also respects current selectedBranchId via fetchData() */}
            <Tooltip title="Refresh">
                            <IconButton
                onClick={fetchData}
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
        {/* ── KPI Cards ──────────────────────────────────────────────────── */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {[
            { icon: <PersonIcon />, title: 'Total Teachers', value: totalTeachers, subtitle: `${activeTeachers} active this week`, color: COLORS.accent },
            { icon: <SchoolIcon />, title: 'Avg Utilization', value: `${avgUtilization}%`, subtitle: 'Across all teachers', color: COLORS.healthy },
            { icon: <CheckCircleIcon />, title: 'Healthy Load', value: healthyCount, subtitle: 'Teachers within ideal range', color: '#00897B' },
            { icon: <WarningIcon />, title: 'Needs Attention', value: summary.filter(t => (t.utilizationPercent || 0) < 5 && (t.totalAssignedPeriods || 0) > 0).length, subtitle: 'Below 5% utilization', color: COLORS.warning },
          ].map((card, i) => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* ── Charts Row ─────────────────────────────────────────────────── */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(11,31,61,0.08)', border: `1px solid ${COLORS.border}` }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary, mb: 0.5 }}>
                  Assigned Periods by Teacher
                </Typography>
                <Typography variant="caption" color="text.secondary">Top 12 teachers by workload</Typography>
                <Box sx={{ mt: 2, height: 220 }}>
                  {loading ? <CircularProgress size={30} /> : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={barData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RTooltip formatter={(v, n) => [v, n === 'assigned' ? 'Assigned' : 'Free']} />
                        <Bar dataKey="assigned" radius={[4, 4, 0, 0]} maxBarSize={36}>
                          {barData.map((entry, i) => (
                            <Cell key={i} fill={entry.util >= 80 ? COLORS.healthy : entry.util >= 40 ? COLORS.accent : '#90CAF9'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(11,31,61,0.08)', border: `1px solid ${COLORS.border}`, height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary, mb: 0.5 }}>
                  Load Distribution
                </Typography>
                <Typography variant="caption" color="text.secondary">By load band</Typography>
                <Box sx={{ height: 220, mt: 1 }}>
                  {loading ? <CircularProgress size={30} /> : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                          {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Legend iconType="circle" iconSize={10} />
                        <RTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Teacher Table ──────────────────────────────────────────────── */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(11,31,61,0.08)', border: `1px solid ${COLORS.border}` }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, pb: 0 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primary }}>
                  All Teachers
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {filteredSummary.length} teachers found
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search teachers..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0); // Reset to first page on search
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: COLORS.border }} />
                    </InputAdornment>
                  ),
                  sx: { maxWidth: 280 }
                }}
              />
            </Box>
            <Divider sx={{ mt: 2 }} />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress sx={{ color: COLORS.primary }} />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 500, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Teacher</TableCell>
                        <TableCell align="center">Assigned Periods</TableCell>
                        <TableCell align="center">Total Periods</TableCell>
                        <TableCell>Utilization</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedSummary.map((teacher, i) => (
                        <TeacherRow key={teacher.teacherId} teacher={teacher} index={i} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={rowsPerPageOptions}
                  component="div"
                  count={filteredSummary.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{ borderTop: `1px solid ${COLORS.border}` }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}